import { createContext, useContext, useEffect, useRef } from 'react'

export interface ModalProps {
  open: boolean
  onClose: () => void
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  variant?: 'default' | 'danger' | 'warning' | 'info'
  theme?: 'light' | 'dark'
  closeOnOverlayClick?: boolean
  closeOnEsc?: boolean
  children: React.ReactNode
}

interface ModalHeaderProps {
  children: React.ReactNode
  showCloseButton?: boolean
  onClose?: () => void
}

interface ModalBodyProps {
  children: React.ReactNode
}

interface ModalFooterProps {
  children: React.ReactNode
}

interface ModalContextValue {
  styles: {
    container: string
    header: string
    footer: string
    text: string
    icon: string
  }
  onClose: () => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

function useModalContext() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('Modal compound components must be used within Modal')
  }
  return context
}

const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-full w-[90vw]',
} as const

const VARIANT_STYLES = {
  light: {
    default: {
      container: 'bg-white border-gray-200',
      header: 'bg-gray-50 border-gray-200',
      footer: 'bg-gray-50 border-gray-200',
      text: 'text-gray-900',
      icon: 'text-gray-600',
    },
    danger: {
      container: 'bg-white border-red-200',
      header: 'bg-red-50 border-red-200',
      footer: 'bg-gray-50 border-gray-200',
      text: 'text-red-700',
      icon: 'text-red-500',
    },
    warning: {
      container: 'bg-white border-amber-200',
      header: 'bg-amber-50 border-amber-200',
      footer: 'bg-gray-50 border-gray-200',
      text: 'text-amber-700',
      icon: 'text-amber-500',
    },
    info: {
      container: 'bg-white border-blue-200',
      header: 'bg-blue-50 border-blue-200',
      footer: 'bg-gray-50 border-gray-200',
      text: 'text-blue-700',
      icon: 'text-blue-500',
    },
  },
  dark: {
    default: {
      container: 'bg-slate-800 border-slate-700',
      header: 'bg-slate-800 border-slate-700',
      footer: 'bg-slate-800 border-slate-700',
      text: 'text-white',
      icon: 'text-slate-400',
    },
    danger: {
      container: 'bg-slate-800 border-slate-700',
      header: 'bg-red-500/10 border-slate-700',
      footer: 'bg-slate-800 border-slate-700',
      text: 'text-red-400',
      icon: 'text-red-400',
    },
    warning: {
      container: 'bg-slate-800 border-slate-700',
      header: 'bg-amber-500/10 border-slate-700',
      footer: 'bg-slate-800 border-slate-700',
      text: 'text-amber-400',
      icon: 'text-amber-400',
    },
    info: {
      container: 'bg-slate-800 border-slate-700',
      header: 'bg-blue-500/10 border-slate-700',
      footer: 'bg-slate-800 border-slate-700',
      text: 'text-blue-400',
      icon: 'text-blue-400',
    },
  },
} as const

/**
 * Flexible modal component
 *
 * Supports multiple sizes, variants, and themes. Use compound components for structure:
 * - Modal.Header for title and close button
 * - Modal.Body for scrollable content
 * - Modal.Footer for action buttons
 *
 * @example
 * ```tsx
 * <Modal open={showModal} onClose={() => setShowModal(false)} size="md" variant="danger">
 *   <Modal.Header>Delete Item</Modal.Header>
 *   <Modal.Body>Are you sure you want to delete this item?</Modal.Body>
 *   <Modal.Footer>
 *     <button onClick={onClose}>Cancel</button>
 *     <button onClick={onDelete}>Delete</button>
 *   </Modal.Footer>
 * </Modal>
 * ```
 */
export function Modal({
  open,
  onClose,
  size = 'md',
  variant = 'default',
  theme = 'light',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  children,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)

  // Handle ESC key
  useEffect(() => {
    if (!open || !closeOnEsc) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, closeOnEsc, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [open])

  // Focus trap - focus modal when opened
  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.focus()
    }
  }, [open])

  if (!open) return null

  const styles = VARIANT_STYLES[theme][variant]
  const sizeClass = SIZE_CLASSES[size]

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <ModalContext.Provider value={{ styles, onClose }}>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
      >
        <div
          ref={modalRef}
          className={`${styles.container} border rounded-2xl shadow-2xl w-full ${sizeClass} max-h-[90vh] overflow-hidden flex flex-col`}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>
  )
}

/**
 * Modal header with optional close button
 */
function ModalHeader({ children, showCloseButton = true, onClose: customOnClose }: ModalHeaderProps) {
  const { styles, onClose: contextOnClose } = useModalContext()
  const handleClose = customOnClose || contextOnClose

  return (
    <div className={`px-6 py-4 border-b flex items-center justify-between ${styles.header}`}>
      <h3 className={`text-lg font-semibold ${styles.text}`}>{children}</h3>
      {showCloseButton && handleClose && (
        <button
          onClick={handleClose}
          className={`${styles.icon} hover:opacity-70 transition-opacity`}
          aria-label="Close modal"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}

/**
 * Modal body with scrollable content
 */
function ModalBody({ children }: ModalBodyProps) {
  return <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
}

/**
 * Modal footer for action buttons
 */
function ModalFooter({ children }: ModalFooterProps) {
  const { styles } = useModalContext()
  return <div className={`px-6 py-4 border-t flex justify-end gap-3 ${styles.footer}`}>{children}</div>
}

// Attach subcomponents
Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter
